import classNames from "classnames";
import { Icons } from "assets";
import { RevisionFile } from "shared/types/revision";
import Spinner from "shared/components/common/spinner";
import styles from "./style.module.scss";

interface ClientInspirationGridProps {
  files: RevisionFile[];
  onImageClick: (fileId: string) => void;
  onDownload: (file: RevisionFile) => void;
  downloadingFileId: string | null;
}

// Read-only mirror of the designer file grid, for the files the client attached
// to their feedback. No reorder, delete or sort — these belong to the client.
const ClientInspirationGrid = ({
  files,
  onImageClick,
  onDownload,
  downloadingFileId,
}: ClientInspirationGridProps) => {
  if (files.length === 0) return null;

  const downloadButton = (file: RevisionFile) => (
    <button
      type="button"
      aria-label={`Download ${file.name}`}
      className={classNames(
        styles.downloadIcon,
        "absolute bottom-2 right-2 flex items-center justify-center",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onDownload(file);
      }}
      disabled={downloadingFileId === file.id}
    >
      {downloadingFileId === file.id ? <Spinner /> : <Icons.Download />}
    </button>
  );

  return (
    <div className={styles.filesGrid}>
      {files.map((file) => (
        <div
          key={file.id}
          className={classNames(
            styles.fileThumbnail,
            "relative",
            file.type === "image" && "cursor-pointer",
          )}
          onClick={() => {
            if (file.type === "image") onImageClick(file.id);
          }}
        >
          {file.type === "image" ? (
            <div className={classNames(styles.imagePlaceholder, "relative")}>
              <img
                src={file.thumbnail}
                alt={file.name}
                draggable={false}
                onError={(e) => {
                  const img = e.currentTarget;
                  if (img.dataset.fallback) return;
                  img.dataset.fallback = "true";
                  img.style.display = "none";
                  img.parentElement
                    ?.querySelector<HTMLElement>(`.${styles.missingThumb}`)
                    ?.style.setProperty("display", "flex");
                }}
              />
              <div
                className={classNames(
                  styles.missingThumb,
                  "absolute inset-0 flex-col items-center justify-center gap-1",
                )}
                style={{ display: "none" }}
              >
                <Icons.TabImage className={styles.missingThumbIcon} />
                <span>Unavailable</span>
              </div>
              {downloadButton(file)}
            </div>
          ) : (
            <div className={classNames(styles.pdfPlaceholder, "relative")}>
              {file.type === "pdf" ? (
                <Icons.PDFIcon className={styles.thumbnailIcon} />
              ) : (
                <Icons.DocumentIcon className={styles.thumbnailIcon} />
              )}
              {downloadButton(file)}
            </div>
          )}
          <span className={styles.fileName}>{file.name}</span>
        </div>
      ))}
    </div>
  );
};

export default ClientInspirationGrid;
